import type { Product, CatalogSort } from '../types/product.ts';

/**
 * Búsqueda abierta del catálogo, hecha aquí y no en la API (que solo sabe "contiene
 * la frase tal cual"). Lo que la hace poco estricta:
 *  - sin acentos ni mayúsculas: "grafica" encuentra "Gráfica";
 *  - sin espacios ni guiones: "rtx5080" encuentra "RTX 5080", "16gb" encuentra "16 GB";
 *  - en español o inglés: "audifonos" encuentra la categoría "Headset", "negro" "Black";
 *  - las palabras en cualquier orden: "5080 rog" encuentra "ROG Astral … RTX 5080";
 *  - errores de tipeo pequeños en palabras (nunca en números: 5070 no es 5080);
 *  - mira nombre, marca, código, categoría y descripción, pesando más el nombre;
 *  - si ningún artículo tiene todas las palabras, se muestran los que tienen más.
 */

/** Minúsculas, sin acentos y solo letras/números separados por un espacio. */
export function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Palabras que no ayudan a distinguir ("silla de gaming", "cable para monitor")
const STOPWORDS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'un', 'una', 'y', 'o', 'en', 'con', 'para', 'por', 'a', 'al',
  'the', 'and', 'for', 'with', 'of',
]);

// El catálogo mezcla idiomas ("Headset", "Power Supply", "Teclado", "Sillas Gaming"):
// buscar una palabra de un grupo también busca las demás.
const SYNONYM_GROUPS: string[][] = [
  ['audifonos', 'audifono', 'auriculares', 'cascos', 'headset', 'headphones'],
  ['raton', 'mouse'],
  ['alfombrilla', 'mousepad', 'mouse pad', 'deskmat'],
  ['teclado', 'keyboard'],
  ['gabinete', 'case', 'chasis', 'caja', 'tower'],
  ['fuente', 'power supply', 'psu'],
  ['grafica', 'gpu', 'tarjeta de video'],
  ['procesador', 'cpu', 'processor'],
  ['memoria', 'ram'],
  ['disco', 'ssd', 'nvme', 'hdd', 'almacenamiento', 'storage'],
  ['motherboard', 'tarjeta madre', 'placa madre', 'placa base', 'mainboard'],
  ['silla', 'chair'],
  ['pantalla', 'monitor', 'display'],
  ['enfriamiento', 'ventilador', 'abanico', 'disipador', 'cooling', 'cooler', 'fan'],
  ['control', 'mando', 'gamepad', 'joystick', 'controller'],
  ['microfono', 'mic', 'microphone'],
  ['camara', 'webcam'],
  ['bocina', 'parlante', 'speaker'],
  ['escritorio', 'desk'],
  ['computadora', 'ordenador', 'ensamble'],
  ['inalambrico', 'inalambrica', 'wireless'],
  ['negro', 'negra', 'black'],
  ['blanco', 'blanca', 'white'],
  ['rosado', 'rosada', 'rosa', 'pink'],
  ['rojo', 'roja', 'red'],
  ['azul', 'blue'],
  ['gris', 'gray', 'grey'],
  ['plata', 'plateado', 'silver'],
  ['verde', 'green'],
  ['morado', 'purple'],
];

const SYNONYMS = new Map<string, string[]>();
for (const group of SYNONYM_GROUPS) {
  for (const word of group) {
    SYNONYMS.set(word, [...(SYNONYMS.get(word) ?? []), ...group.filter((w) => w !== word)]);
  }
}

interface Variant {
  term: string;
  factor: number;
}

/** La palabra buscada, su singular ("sillas" → "silla") y sus sinónimos. */
function variants(token: string): Variant[] {
  const out: Variant[] = [{ term: token, factor: 1 }];
  if (/^[a-z]{5,}$/.test(token)) {
    if (token.endsWith('es')) out.push({ term: token.slice(0, -2), factor: 0.95 });
    if (token.endsWith('s')) out.push({ term: token.slice(0, -1), factor: 0.95 });
  }
  for (const v of [...out]) {
    for (const s of SYNONYMS.get(v.term) ?? []) out.push({ term: s, factor: 0.9 });
  }
  const seen = new Set<string>();
  return out.filter((v) => !seen.has(v.term) && seen.add(v.term));
}

// Frases de varias palabras ("tarjeta de video", "tarjetas madre", "power supply"):
// se cambian por una palabra de su grupo antes de partir la búsqueda, si no "video"
// se buscaría por separado.
const PHRASES = SYNONYM_GROUPS.flatMap((group) => {
  const single = group.find((w) => !w.includes(' '));
  return single
    ? group
        .filter((w) => w.includes(' '))
        .map((phrase) => ({
          pattern: new RegExp(`\\b${phrase.split(' ').map((w) => `${w}(?:e?s)?`).join(' ')}\\b`, 'g'),
          single,
        }))
    : [];
});

function queryTokens(query: string): string[] {
  let text = normalizeText(query);
  for (const { pattern, single } of PHRASES) text = text.replace(pattern, single);
  const words = text.split(' ').filter(Boolean);
  const useful = words.filter((w) => !STOPWORDS.has(w));
  // "de" solo, por ejemplo: mejor buscarlo que no buscar nada
  return [...new Set(useful.length > 0 ? useful : words)];
}

interface Field {
  text: string;     // normalizado, con espacios
  compact: string;  // normalizado, sin espacios
  words: string[];
  weight: number;
}

interface Indexed {
  fields: Field[];
}

const index = new WeakMap<Product, Indexed>();

function field(text: string | null | undefined, weight: number): Field | null {
  const norm = normalizeText(text ?? '');
  if (!norm) return null;
  const words = norm.split(' ');
  // "16gb", "wifi7", "rtx5060": también cuentan sus partes ("gb", "7", "5060")
  const parts = words.flatMap((w) => w.match(/[a-z]+|[0-9]+/g) ?? []).filter((w) => !words.includes(w));
  return { text: norm, compact: norm.replace(/ /g, ''), words: [...words, ...new Set(parts)], weight };
}

function indexed(product: Product): Indexed {
  let entry = index.get(product);
  if (!entry) {
    entry = {
      fields: [
        field(product.name, 10),
        field(product.brand, 8),
        field(product.code ?? product.sku, 8),
        field(product.productSubCategory ?? product.category, 6),
        field(product.description, 2),
      ].filter((f): f is Field => f !== null),
    };
    index.set(product, entry);
  }
  return entry;
}

/** Distancia de edición con transposiciones ("gefroce" → "geforce" = 1). */
function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const rows: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) rows[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    let rowMin = Infinity;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let d = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d = Math.min(d, rows[i - 2][j - 2] + 1);
      }
      rows[i][j] = d;
      rowMin = Math.min(rowMin, d);
    }
    if (rowMin > max) return max + 1;
  }
  return rows[a.length][b.length];
}

/** Errores de tipeo tolerados: ninguno en palabras cortas, 1 hasta 6 letras, 2 desde 7. */
function typoBudget(token: string): number {
  if (!/^[a-z]+$/.test(token) || token.length < 4) return 0;
  return token.length >= 7 ? 2 : 1;
}

/** Qué tan bien aparece un término en un campo (0 = no aparece). */
function fieldMatch(term: string, f: Field): number {
  if (f.text === term) return 2.2; // el campo entero: categoría "Mouse", marca "Corsair"
  if (term.includes(' ')) return f.text.includes(term) || f.compact.includes(term.replace(/ /g, '')) ? 1.2 : 0;
  if (f.words.includes(term)) return 1.6;
  if (f.words.some((w) => w.startsWith(term))) return 1.1;
  // "i7" o "gb" sueltos dentro de otra palabra ("wifi7") son casualidad
  if (term.length < 3) return 0;
  if (f.text.includes(term)) return 0.8;
  // Pegado: "rtx5080" ~ "rtx 5080". Nunca números solos (pegaría números de palabras
  // distintas: "ryzen 5 5080…") ni en la descripción, que es texto suelto.
  if (!/^[0-9]+$/.test(term) && f.weight > 2 && f.compact.includes(term)) return 0.8;
  return 0;
}

/** Puntos que suma una palabra buscada en un artículo (0 = no aparece). */
function tokenScore(token: string, tokenVariants: Variant[], fields: Field[]): number {
  let best = 0;
  for (const v of tokenVariants) {
    for (const f of fields) {
      best = Math.max(best, f.weight * fieldMatch(v.term, f) * v.factor);
    }
  }
  if (best > 0) return best;

  const budget = typoBudget(token);
  if (budget === 0) return 0;
  for (const f of fields) {
    // La descripción es larga y suelta: con ella los errores de tipeo dan falsos positivos
    if (f.weight <= 2) continue;
    for (const w of f.words) {
      if (!/^[a-z]+$/.test(w)) continue;
      // Compara con la palabra completa y con su comienzo ("logitec" ~ "logitech")
      const prefix = w.slice(0, token.length);
      if (editDistance(token, w, budget) <= budget || editDistance(token, prefix, budget) <= budget) {
        best = Math.max(best, f.weight * 0.6);
      }
    }
  }
  return best;
}

/**
 * Los artículos que coinciden con la búsqueda, del más relevante al menos. A igual
 * relevancia se conserva el orden de entrada (el catálogo llega del más nuevo al
 * más viejo). Búsqueda vacía = la lista tal cual.
 */
export function searchProducts(products: readonly Product[], query: string): Product[] {
  const tokens = queryTokens(query);
  if (tokens.length === 0) return [...products];
  const tokenVariants = tokens.map(variants);

  const scored = products.map((product, order) => {
    const { fields } = indexed(product);
    let score = 0;
    let matched = 0;
    let matchedLong = 0;
    for (const [i, t] of tokens.entries()) {
      const s = tokenScore(t, tokenVariants[i], fields);
      if (s > 0) {
        score += s;
        matched += 1;
        if (t.length >= 3) matchedLong += 1;
      }
    }
    // Todo lo buscado junto y en orden en el nombre ("rtx 5080"): arriba del todo
    const name = fields[0];
    const phrase = tokens.join(' ');
    if (tokens.length > 1 && name && (name.text.includes(phrase) || name.compact.includes(phrase.replace(/ /g, '')))) {
      score += 10;
    }
    return { product, order, score, matched, matchedLong };
  });

  const all = scored.filter((s) => s.matched === tokens.length);
  // Nada tiene todas las palabras: lo que tenga más, pero al menos una palabra con
  // sustancia (que "ryzen 5 9999" no devuelva todo lo que tenga un 5)
  const results = all.length > 0
    ? all
    : scored.filter((s) => s.matchedLong > 0 || (tokens.every((t) => t.length < 3) && s.matched > 0));

  return results
    .sort((a, b) => b.matched - a.matched || b.score - a.score || a.order - b.order)
    .map((s) => s.product);
}

/** Orden del catálogo aplicado aquí (la búsqueda ya no pasa por la API). */
export function sortProducts(products: readonly Product[], sorts: readonly CatalogSort[] | undefined): Product[] {
  const sort = sorts?.[0];
  if (!sort) return [...products];
  const dir = sort.sortDirection === 'desc' ? -1 : 1;
  const text = (p: Product): string => {
    switch (sort.sortBy) {
      case 'brand': return p.brand ?? '';
      case 'productSubCategory': return p.productSubCategory ?? p.category ?? '';
      default: return p.name;
    }
  };
  return [...products].sort((a, b) =>
    sort.sortBy === 'price'
      ? (a.price - b.price) * dir
      : text(a).localeCompare(text(b), 'es', { sensitivity: 'base' }) * dir,
  );
}
